import dayjs from 'dayjs'
import { convertPaisaToRupee } from 'state/billing/billing.helpers'
import { IKeyVals } from 'state/tags/tags.types'
import {
  IOrderSummaryDateFilter,
  IOrderSummaryFormattedData,
  IOrderSummaryGroupFormattedData,
  IOrderSummaryItemLevelStats,
  IOrderSummaryItemLevelStatsV2,
  IOrderSummaryListFormattedData,
  IOrderSummaryStats
} from './orderSummary.types'

export const formatOrderSummaryRowData = (data: any): IOrderSummaryFormattedData => {
  return {
    groupName: data.group_name,
    totalOrders: Number(data.total),
    totalAmount: Number(data.total_amount) / 100,
    totalAcceptedAmount: Number(data.total_accepted_amount) / 100,
    totalPendingAmount: Number(data.total_pending_amount) / 100
  }
}

export const formatOrderSummaryListRowData = (data: any[]): IOrderSummaryListFormattedData[] =>
  data.map((item) => ({
    groupName: item.group_name,
    totalOrders: Number(item.total),
    totalAmount: Number(item.total_amount) / 100,
    totalAcceptedAmount: Number(item.total_accepted_amount) / 100,
    totalPendingAmount: Number(item.total_pending_amount) / 100,
    groupId: item.group_id
  }))

export const formatOrderSummaryListRowDataDownload = (data: any[]): any[] =>
  data.map((item) => ({
    'Group Name': item.group_name,
    'Total Orders': Number(item.total),
    'Total Amount': Number(item.total_amount) / 100,
    'Total Accepted': Number(item.total_accepted_amount) / 100,
    'Total Pending': Number(item.total_pending_amount) / 100
  }))

export const formatOrderGroupSummaryRowData = (data: any): IOrderSummaryGroupFormattedData => ({
  groupName: data.group_name,
  totalOrders: Number(data.total),
  totalAmount: Number(data.total_amount) / 100,
  totalAcceptedAmount: Number(data.total_accepted_amount) / 100,
  totalPendingAmount: Number(data.total_pending_amount) / 100,
  groupId: data.group_id,
  orderDetails: data?.order_details?.map((order: any) => ({
    number: order.number,
    customerName: order.customer_name,
    amount: Number(order.amount) / 100,
    status: order.status,
    createTime: dayjs(Number(order.create_time) * 1000).format('DD-MMM-YYYY'),
    updateTime: dayjs(Number(order.update_time) * 1000).format('DD-MMM-YYYY')
  }))
})

const statusMap = [
  'DEFAULT',
  'PENDING',
  'APPROVED',
  'PROFORMA_CREATED',
  'INVOICED',
  'CANCELLED',
  'REJECTED',
  'OUT_FOR_DELIVERY',
  'COMPLETED',
  'INVOICED_EXT'
]

export const formatOrderGroupSummaryRowDataDownload = (data: any): any[] =>
  data?.order_details?.map((order: any) => ({
    'Group Name': data.group_name,
    'Total Orders': Number(data.total),
    'Total Amount': Number(data.total_amount) / 100,
    'Total Accepted': Number(data.total_accepted_amount) / 100,
    'Total Pending': Number(data.total_pending_amount) / 100,
    'Order Number': order.number,
    'Customer Name': order.customer_name,
    Amount: Number(order.amount) / 100,
    Status: statusMap[Number(order?.status)] || 'DEFAULT',
    Date: dayjs(Number(order.create_time) * 1000).format('DD-MMM-YYYY'),
    'Last Action': dayjs(Number(order.update_time) * 1000).format('DD-MMM-YYYY')
  }))

/** @deprecated */
export const getFormattedOverallOrderGroupSummary = (
  data: IOrderSummaryItemLevelStats[]
): IOrderSummaryItemLevelStats[] =>
  data.map((item) => ({
    ...item,
    rate: convertPaisaToRupee(item.rate),
    order_item_value: convertPaisaToRupee(item.order_item_value)
  }))

export const getFormattedOverallOrderGroupSummaryV2 = (
  data: IOrderSummaryItemLevelStatsV2[],
  accontLabels: IKeyVals[]
): IOrderSummaryItemLevelStatsV2[] => {
  data.forEach((element, index) => {
    let updatedLabelsObject: any = {}
    Object.keys(element.labels || {}).forEach((labelId) => {
      const labelObject = accontLabels.find((label) => label.key_id === labelId)
      const labelName = labelObject?.key || ''
      const labelIdToValueMapper: any = {}
      labelObject?.vals.forEach((val) => {
        labelIdToValueMapper[val.val_id] = val.val
      })
      updatedLabelsObject = {
        ...updatedLabelsObject,
        [labelName]: element.labels?.[labelId].map(
          (labelValueId) => labelIdToValueMapper[labelValueId]
        )
      }
    })

    data[index] = {
      ...data[index],
      updatedLabelsObject
    }
  })

  return data.map((item) => ({
    ...item,
    rate: convertPaisaToRupee(item.rate),
    order_item_value: convertPaisaToRupee(item.order_item_value)
  }))
}

export const getFormattedOuterOrderGroupSummary = (
  data: IOrderSummaryStats
): IOrderSummaryStats => ({
  ...data,
  total_orders_value: convertPaisaToRupee(Number(data.total_orders_value || 0)),
  total_pending_orders_value: convertPaisaToRupee(Number(data.total_pending_orders_value || 0)),
  total_ordered_qty: data.total_ordered_qty || 0,
  total_pending_qty: data.total_pending_qty || 0,
  total_accepted_qty: data.total_accepted_qty || 0,
  total_rejected_qty: data.total_rejected_qty || 0
})

export const getExcelHeading = (
  activeFilter: IOrderSummaryDateFilter | null,
  title: string
): string => {
  if (activeFilter && activeFilter.startTime && activeFilter.endTime) {
    return `${title} (${dayjs.unix(activeFilter.startTime).format('DD MMM, YYYY')} - ${dayjs
      .unix(activeFilter.endTime)
      .format('DD MMM, YYYY')})`
  }
  return title
}
const keyMapper: { [key in keyof IOrderSummaryItemLevelStats]?: string } = {
  order_number: 'Order Number',
  customer_name: 'Customer Name',
  zone: 'Zone',
  area: 'Area',
  // sales_person: 'Sales Person',
  item_category: 'Item Category',
  item_name: 'Item Name',
  sku: 'SKU',
  rate: 'Rate',
  ordered_quantity: 'Ordered Qty',
  order_item_value: 'Value'
}

export const formatDataForExcel = (data: IOrderSummaryItemLevelStatsV2[]) => {
  return data.map((obj) => {
    return (Object.keys(obj) as (keyof IOrderSummaryItemLevelStatsV2)[]).reduce(
      (acc: { [key: string]: any }, key) => {
        if (obj[key] !== undefined) {
          // Ensure the value exists
          if (key === 'updatedLabelsObject') {
            Object.keys(obj.updatedLabelsObject || {}).forEach((labelName) => {
              acc[labelName] = ((obj.updatedLabelsObject || {})[labelName] || []).join(', ')
            })
          } else if (key !== 'labels') {
            const newKey = keyMapper[key] || key // Use mapped key or keep the original
            acc[newKey] = obj[key]
          }
        }
        return acc
      },
      {}
    )
  })
}

// Function to adjust column width
export const getOrderColumnWidths = (data: any[]) => {
  return (
    data[0] &&
    Object.keys(data[0]).map((key) => {
      const maxLength = data.reduce((max, row) => {
        const cellContent = row[key] ? row[key].toString() : '' // Convert to string if needed
        return Math.max(max, cellContent.length)
      }, key.length) // Include header length in calculation
      return { wch: maxLength + 2 } // Add some padding
    })
  )
}
